import { alpha, createTheme } from '@mui/material/styles'

/**
 * MVC Games Unified Theme
 * 
 * Primary: #003976 (Navy Blue) - Trust, sports tradition, professionalism
 * Error: #C8102E (Cardinal Red) - Attention, urgency, errors
 * 
 * This theme provides consistent styling across the entire application
 * with accessibility, sports aesthetics, and modern UX patterns in mind.
 */

export const theme = createTheme({
    palette: {
        primary: {
            main: '#003976',
            light: '#1a5496',
            dark: '#002654',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#6B7280',
            light: '#9CA3AF',
            dark: '#4B5563',
            contrastText: '#ffffff',
        },
        error: {
            main: '#C8102E',
            light: '#DC143C',
            dark: '#A00C24',
            contrastText: '#ffffff',
        },
        warning: {
            main: '#D97706',
            light: '#F59E0B',
            dark: '#B45309',
            contrastText: '#ffffff',
        },
        info: {
            main: '#0284C7',
            light: '#0EA5E9',
            dark: '#075985',
            contrastText: '#ffffff',
        },
        success: {
            main: '#059669',
            light: '#10B981',
            dark: '#047857',
            contrastText: '#ffffff',
        },
        background: {
            default: '#F9FAFB',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#111827',
            secondary: '#6B7280',
            disabled: '#9CA3AF',
        },
        divider: '#E5E7EB',
    },

    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',

        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.01562em',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: '-0.00833em',
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '0em',
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.35,
            letterSpacing: '0.00735em',
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: '0em',
        },
        h6: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.45,
            letterSpacing: '0.0075em',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
            letterSpacing: '0.00938em',
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.57,
            letterSpacing: '0.00714em',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.00938em',
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.57,
            letterSpacing: '0.01071em',
        },
        button: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.75,
            letterSpacing: '0.02857em',
            textTransform: 'none',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.66,
            letterSpacing: '0.03333em',
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 2.66,
            letterSpacing: '0.08333em',
            textTransform: 'uppercase',
        },
    },

    shape: {
        borderRadius: 8,
    },

    shadows: [
        'none',
        '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    ],

    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    },
                },
                containedPrimary: {
                    '&:hover': {
                        backgroundColor: '#1a5496',
                    },
                },
                outlined: {
                    borderWidth: 2,
                    '&:hover': {
                        borderWidth: 2,
                    },
                },
            },
            defaultProps: {
                disableElevation: true,
            },
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    borderRadius: 12,
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    },
                },
            },
        },

        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                elevation1: {
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                },
                elevation2: {
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                },
                elevation3: {
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: '#003976',
                        },
                    },
                },
            },
            defaultProps: {
                variant: 'outlined',
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                    },
                },
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
                filled: {
                    '&.MuiChip-colorPrimary': {
                        backgroundColor: alpha('#003976', 0.1),
                        color: '#003976',
                    },
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #E5E7EB',
                },
                head: {
                    fontWeight: 600,
                    backgroundColor: '#F9FAFB',
                    color: '#111827',
                },
            },
        },

        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: '#F9FAFB',
                    },
                    '&:hover': {
                        backgroundColor: alpha('#003976', 0.04),
                    },
                },
            },
        },

        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                standardError: {
                    backgroundColor: alpha('#C8102E', 0.1),
                    color: '#A00C24',
                },
                standardWarning: {
                    backgroundColor: alpha('#D97706', 0.1),
                    color: '#B45309',
                },
                standardInfo: {
                    backgroundColor: alpha('#0284C7', 0.1),
                    color: '#075985',
                },
                standardSuccess: {
                    backgroundColor: alpha('#059669', 0.1),
                    color: '#047857',
                },
            },
        },

        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                },
                colorPrimary: {
                    backgroundColor: '#003976',
                },
            },
        },

        MuiTabs: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #E5E7EB',
                },
                indicator: {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                },
            },
        },

        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minHeight: 48,
                },
            },
        },

        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#E5E7EB',
                },
            },
        },

        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#003976',
                    textDecoration: 'none',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            },
            defaultProps: {
                underline: 'hover',
            },
        },

        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                },
            },
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#111827',
                    fontSize: '0.75rem',
                    borderRadius: 6,
                },
                arrow: {
                    color: '#111827',
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                },
            },
        },

        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: '1.25rem',
                },
            },
        },
    },
})
